import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sun, Moon, Translate, Headset, ArrowRight } from "@phosphor-icons/react";
import SdmLogo from "@/components/SdmLogo";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import { AuthHeroDesktop, AuthHeroMobile } from "@/components/AuthHero";


export default function Login() {
  const { user, login, loading } = useAuth();
  const { t, lang, toggle: toggleLang } = useLang();
  const { theme, toggle: toggleTheme } = useTheme();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Remember-me defaults to ON so returning users stay signed in across tabs.
  const [remember, setRemember] = useState(true);
  const [forgotOpen, setForgotOpen] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    try {
      await login(email, password, remember);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("auth.error"));
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* LEFT — form */}
      <div className="relative flex flex-col justify-between p-8 lg:p-14">
        <AuthHeroMobile />

        {/* Brand */}
        <div className="flex items-start justify-between">
          <SdmLogo size={56} />
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={toggleLang} data-testid="lang-toggle-login" className="gap-1 rounded-full">
              <Translate size={16} />
              <span className="text-xs uppercase font-semibold">{lang}</span>
            </Button>
            <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle-login" className="rounded-full">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
          </div>
        </div>

        {/* Center */}
        <div className="w-full max-w-lg mx-auto lg:mx-0 py-16">
          {/* App tile */}
          <div className="flex items-center gap-3 mb-10 animate-fade-in">
            <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center shrink-0">
              <Headset size={24} weight="duotone" className="text-accent-foreground"/>
            </div>
            <div>
              <div className="font-heading font-extrabold text-lg leading-tight tracking-[-0.03em]">SDM Customer Engagement Console</div>
            </div>
          </div>

          <h1 className="font-heading text-5xl font-extrabold tracking-[-0.035em] leading-[1.02] mb-3 animate-fade-in whitespace-nowrap">
            {lang === "es" ? "Bienvenido de vuelta" : "Welcome back"}
          </h1>
          <p className="text-base text-muted-foreground mb-10 max-w-md">
            {lang === "es"
              ? "Accede al panel de Servicio al cliente del BDC."
              : "Access the BDC customer service console."}
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 h-14 rounded-xl bg-input border-0 text-base px-5 focus-visible:ring-2 focus-visible:ring-primary"
                autoComplete="email"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">
                {lang === "es" ? "Contraseña" : "Password"}
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 h-14 rounded-xl bg-input border-0 text-base px-5 focus-visible:ring-2 focus-visible:ring-primary"
                autoComplete="current-password"
                required
              />
            </div>

            {/* Remember me + Forgot password — SDM yellow accent on black */}
            <div className="flex items-center justify-between pt-1">
              <label
                htmlFor="remember-me"
                className="group inline-flex items-center gap-2.5 cursor-pointer select-none"
              >
                <span className="relative inline-flex items-center justify-center">
                  <input
                    id="remember-me"
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="peer sr-only"
                    data-testid="login-remember"
                  />
                  <span className="w-5 h-5 rounded-md border-2 border-foreground/70 bg-transparent transition-colors peer-checked:bg-accent peer-checked:border-accent peer-focus-visible:ring-2 peer-focus-visible:ring-accent/50 flex items-center justify-center">
                    <svg
                      className="w-3 h-3 text-black opacity-0 peer-checked:opacity-100 transition-opacity"
                      style={{ opacity: remember ? 1 : 0 }}
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="4 11 8 15 16 6" />
                    </svg>
                  </span>
                </span>
                <span className="text-sm font-medium text-foreground">
                  {lang === "es" ? "Recordarme" : "Remember me"}
                </span>
              </label>
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm font-semibold text-accent hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 rounded"
                data-testid="login-forgot-btn"
              >
                {lang === "es" ? "¿Olvidaste tu contraseña?" : "Forgot your password?"}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 rounded-xl text-base font-semibold gap-2 group"
              data-testid="login-submit-btn"
            >
              {loading ? t("common.loading") : (lang === "es" ? "Iniciar sesión" : "Sign in")}
            </Button>
          </form>
        </div>

        {/* Bottom */}
        <div className="text-xs text-muted-foreground">
          © 2026 · Santo Domingo Motors · República Dominicana
        </div>
      </div>

      <AuthHeroDesktop />

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </div>
  );
}
